import React, { FC, useContext, useState } from 'react';
import CustomDialog from '../../common/components/CustomDialog';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import {
  GetBusinessDetails,
  GetBusinessDetailsVariables,
} from '../../graphql/business/types/GetBusinessDetails';
import { GET_BUSINESS_DETAILS } from '../../graphql/business/query';
import BusinessDetailHeader from './Header/BusinessDetailHeader';
import { formatDate, getFullName, mapBusinessDataToState } from './utils/utils';
import BusinessCoordonnees from './Content/BusinessCoordonnees';
import ConfirmationButtons from '../ConfirmationButtons/ConfirmationButtons';
import {
  UpdateBusiness,
  UpdateBusinessVariables,
} from '../../graphql/business/types/UpdateBusiness';
import {
  CHANGE_BUSSINESS_DATES,
  CHANGE_BUSSINESS_ESTIMATIONS,
  UPDATE_BUSINESS,
} from '../../graphql/business/mutation';
import { displaySnackBar } from '../../common/displaySnackbar';
import BusinessRemuneration from './Content/BusinessRemuneration';
import BusinessCommissions from './Content/BusinessCommissions';
import {
  CommissionInput,
  RemunerationInput,
  StepName,
} from '../../types/graphql-global-types';
import { IUserContext, UserContext } from '../../providers/UserProvider';
import {
  GraphqlError,
  Operation,
  Resource,
  Role,
} from '../../common/constants';
import BusinessStatus from './Content/BusinessStatus';
import {
  ChangeBusinessDates,
  ChangeBusinessDatesVariables,
} from '../../graphql/business/types/ChangeBusinessDates';
import { BusinessConnectCreditReferency } from './Content/BusinessConnectCreditReferency';
import { BusinessContext } from './BusinessProvider/BusinessProvider';
import {
  ChangeBusinessEstimations,
  ChangeBusinessEstimationsVariables,
} from '../../graphql/business/types/ChangeBusinessEstimations';

interface Props {
  handleClose?(): void;
  businessId: number;
}

const BusinessDetailsInfosModal: FC<Props> = (props) => {
  const { handleClose, businessId } = props;
  const { businessState, setBusinessState } = useContext(BusinessContext);
  const [agencies, setAgencies] = useState<string[]>([]);
  const { canI } = useContext<IUserContext>(UserContext);
  const {
    me: { roles },
  } = useContext<IUserContext>(UserContext);

  const client = useApolloClient();

  const { data: dataUser, loading: loadingQuery } = useQuery<
    GetBusinessDetails,
    GetBusinessDetailsVariables
  >(GET_BUSINESS_DETAILS, {
    variables: { id: businessId },
    onCompleted: (data) => {
      if (data && data.getBusiness) {
        setBusinessState(mapBusinessDataToState(data.getBusiness));
        const _agencies =
          data?.getBusiness?.apa?.agencies?.reduce(
            (acc, current) => (current?.name ? [...acc, current?.name] : acc),
            [] as string[],
          ) || [];
        setAgencies(_agencies);
      }
    },
  });

  const [updateBusiness, { loading: loadingUpdate }] = useMutation<
    UpdateBusiness,
    UpdateBusinessVariables
  >(UPDATE_BUSINESS, {
    onCompleted: () => {
      displaySnackBar(client, {
        type: 'SUCCESS',
        message: 'Affaire modifiée avec succès',
        isOpen: true,
      });

      if (handleClose) handleClose();
    },
    onError: (error) => {
      if (error.graphQLErrors.length) {
        if (error.graphQLErrors[0].extensions) {
          const code = error.graphQLErrors[0].extensions['original']['name'];
          if (code === GraphqlError.CRM_ID_EXIST) {
            displaySnackBar(client, {
              type: 'ERROR',
              message: 'idCRM existant',
              isOpen: true,
            });
          }
        }
      }
    },
  });

  const [changeDates] = useMutation<
    ChangeBusinessDates,
    ChangeBusinessDatesVariables
  >(CHANGE_BUSSINESS_DATES);

  const [changeEstimations] = useMutation<
    ChangeBusinessEstimations,
    ChangeBusinessEstimationsVariables
  >(CHANGE_BUSSINESS_ESTIMATIONS);

  const handleSave = () => {
    const {
      email,
      phone,
      firstName,
      lastName,
      remunerationId,
      clientHonorary,
      bank,
      bankCommission,
      mandaCommissions,
      apaCommissions,
      step,
      idCRM,
      unblockedAt,
      signedByNotaryAt,
      apa,
      mandatary,
      manager,
      estimatedMaxPurchase,
      estimatedMaxLoan,
    } = businessState;

    const mandaCommissionsNotNull =
      mandaCommissions?.map((commission) => ({
        ...commission,
        id: commission.isNew ? null : commission?.id,
      })) || [];
    const apaCommissionsNotNull =
      apaCommissions?.map((commission) => ({
        ...commission,
        id: commission.isNew ? null : commission?.id,
      })) || [];
    const allCommissions = [
      ...mandaCommissionsNotNull,
      ...apaCommissionsNotNull,
    ];

    const commissions: CommissionInput[] | null = allCommissions.length
      ? allCommissions.map((commission) => ({
          commissionId: commission.id,
          beneficiaryId: commission.beneficiary?.id,
          amount: commission.amount,
        }))
      : null;

    const remuneration: RemunerationInput | null | undefined =
      !remunerationId && !clientHonorary && !bankCommission && !bank
        ? undefined
        : {
            remunerationId,
            clientHonorary,
            bankCommission,
            bankId: bank?.id,
          };

    updateBusiness({
      variables: {
        input: {
          businessId,
          email,
          phone,
          firstName,
          lastName,
          remuneration,
          commissions,
          idCRM: idCRM ? +idCRM : null,
          isBackoffice: true,
          status: step.name as StepName,
          apaId: apa?.id,
          mandataryId: mandatary?.id,
          managerId: manager?.id,
        },
      },
    });

    changeDates({
      variables: {
        input: {
          businessId,
          unblockedAt,
          signedByNotaryAt,
        },
      },
    });

    changeEstimations({
      variables: {
        input: {
          businessId,
          estimatedMaxPurchase,
          estimatedMaxLoan,
        },
      },
    });
  };

  const canEditBusiness = canI(Operation.UPDATE, Resource.BUSINESSES);
  const isExclusiveMandatary = !!roles?.every(
    (role) => role === Role.MANDATARY,
  );

  return (
    <CustomDialog loading={false} onClose={handleClose} open={true}>
      <BusinessDetailHeader
        fullName={getFullName({
          firstName: businessState.firstName,
          lastName: businessState.lastName,
          firstNameFirt: true,
        })}
        apaName={getFullName({
          firstName: businessState.apa.firstName,
          lastName: businessState.apa.lastName,
        })}
        mandataryName={getFullName({
          firstName: businessState.mandatary?.firstName || '',
          lastName: businessState.mandatary?.lastName || '',
        })}
        createdAt={formatDate(businessState.createdAt)}
        apaEnseigneName={businessState.apa.enseigneName}
        status={businessState.step.label}
        agencies={agencies}
        businessState={businessState}
        setBusinessState={setBusinessState}
        CRMStatus={businessState?.CRMStep?.label}
      />
      <BusinessCoordonnees
        businessState={businessState}
        setBusinessState={setBusinessState}
        canEditBusiness={canEditBusiness}
      />

      <BusinessStatus
        businessState={businessState}
        setBusinessState={setBusinessState}
        possibleSteps={dataUser?.getBusiness.possibleSteps || []}
        canEditBusiness={canEditBusiness}
      />

      <BusinessConnectCreditReferency />

      <BusinessRemuneration
        businessState={businessState}
        setBusinessState={setBusinessState}
        canEditBusiness={canEditBusiness}
      />

      <BusinessCommissions
        businessState={businessState}
        setBusinessState={setBusinessState}
        canEditBusiness={canEditBusiness && !isExclusiveMandatary}
      />

      <ConfirmationButtons
        containerStyle={{ marginTop: 20 }}
        handleCancel={handleClose}
        handleConfirm={handleSave}
        disableConfirm={loadingQuery || loadingUpdate}
      />
    </CustomDialog>
  );
};

export default BusinessDetailsInfosModal;
